import { useCallback, useState } from 'react'
import { ApiRetailerPaymentType } from '@vend/types'
import { PAYMENT_TYPES } from '@vend/business-components'
import trim from 'lodash/trim'
import {
  startPerformanceMeasure,
  stopPerformanceMeasure,
  trackUserEvent,
} from '@vend/utilities'
import { PaymentOptions } from '../PaymentModal/types'
import { getFeatureFromCache } from '../../../../util/feature-cache'
import { shouldUseDirectEconduit } from '../../../../functions/payment-types/econduit'
import { WEB_REGISTER } from '../../../web-register.constant'
import { selectSaleLocalId } from '../../../../state/slices/currentSale/currentSaleSlice'
import { useAppSelector } from '../../../../state/hooks'
import { useTrainingModeManager } from '../../../../ng-compat/hooks'
import { useHandleCashPayment } from './useHandleCashPayment'
import { useHandleLSPay } from './useHandleLSPay'
import { useHandleIntegrationPayment } from './useHandleIntegrationPayment'
import { useHandleSwiperPayment } from './useHandleSwiperPayment'
import { useHandleBigCommercePayment } from './useHandleBigCommercePayment'
import { useHandleLoyaltyPayment } from './useHandleLoyaltyPayment'
import { useHandleStoreCreditPayment } from './useHandleStoreCreditPayment'
import { useHandleCompletePayment } from './useHandleCompletePayment'

type SelectedPaymentType = {
  paymentOptions: PaymentOptions
}

export const useHandlePaymentType = (
  amountTendered: string,
  paymentProcessing: boolean,
  hasPaymentAmountError: boolean,
  setPaymentProcessing: (value: boolean) => void,
  allowPageReload: () => void,
  updateTenderAmount: (amount?: number | string) => void
) => {
  const [
    selectedPaymentType,
    setSelectedPaymentType,
  ] = useState<SelectedPaymentType | null>(null)
  const localId = useAppSelector(selectSaleLocalId)
  const trainingModeManager = useTrainingModeManager()

  const clearSelectedPaymentType = () => {
    allowPageReload()
    setPaymentProcessing(false)
    setSelectedPaymentType(null)
  }

  const { handleCompletePayment } = useHandleCompletePayment(
    allowPageReload,
    clearSelectedPaymentType,
    updateTenderAmount
  )

  const { handleCashPayment } = useHandleCashPayment(
    amountTendered,
    handleCompletePayment
  )
  const { handleLSPay } = useHandleLSPay(amountTendered)
  const { handleIntegrationPayment } = useHandleIntegrationPayment(
    amountTendered
  )
  const { handleSwiperPayment } = useHandleSwiperPayment(amountTendered)
  const { handleBigCommercePayment } = useHandleBigCommercePayment(
    amountTendered
  )
  const { handleLoyaltyPayment } = useHandleLoyaltyPayment(
    amountTendered,
    handleCompletePayment
  )
  const { handleStoreCreditPayment } = useHandleStoreCreditPayment(
    amountTendered,
    handleCompletePayment
  )

  const defaultPaymentHandler = useCallback(
    (paymentType: ApiRetailerPaymentType) => {
      const paymentTypeConfig = paymentType.config
      const hasPaymentTypeUrl = paymentTypeConfig && trim(paymentTypeConfig.url)

      if (hasPaymentTypeUrl) {
        if (
          shouldUseDirectEconduit(paymentType) &&
          getFeatureFromCache('direct_econduit')
        ) {
          return handleSwiperPayment(paymentType)
        } else {
          // All integrated payment types will be here due to the config url
          return handleIntegrationPayment(paymentType)
        }
      } else {
        handleCompletePayment({
          paymentAmount: amountTendered,
          paymentType,
        })
      }
    },
    [
      amountTendered,
      handleCompletePayment,
      handleIntegrationPayment,
      handleSwiperPayment,
    ]
  )

  const handlePaymentTracking = useCallback(
    (payment: ApiRetailerPaymentType, isTopLSpay = false) => {
      // stop the timing when the user selects a payment type
      const measure = stopPerformanceMeasure('select-payment-type-process')
      trackUserEvent(WEB_REGISTER.DISPLAY_NAME, 'Payment Type Selected', {
        amount: amountTendered,
        registerSaleId: localId,
        startTime: measure?.startTime || null,
        duration: measure?.duration || null,
        paymentTypeName: payment.name,
        paymentTypeId: payment.type_id,
        retailerPaymentTypeId: payment.id,
        isQuickPay: false,
        isTopLSpay,
        quickPayFeatureFlag: getFeatureFromCache(
          'lspay_pay_with_quick_pay_experiment'
        ),
      })

      if (trainingModeManager.trainingModeEnabled) {
        trackUserEvent(
          WEB_REGISTER.DISPLAY_NAME,
          trainingModeManager.currentTourType?.toString() || '',
          {
            metadata: {
              step: 'Selected Payment Type',
              paymentType: payment.name,
            },
          }
        )
      } else {
        startPerformanceMeasure('payment-tender')
      }
    },
    [
      amountTendered,
      localId,
      trainingModeManager.currentTourType,
      trainingModeManager.trainingModeEnabled,
    ]
  )

  const handlePayment = useCallback(
    (payment: ApiRetailerPaymentType, isTopLSpay = false) => {
      if (paymentProcessing) {
        return
      }
      if (hasPaymentAmountError) {
        return
      }

      handlePaymentTracking(payment, isTopLSpay)
      setPaymentProcessing(true)

      let paymentOptions
      switch (payment.type_id) {
        case PAYMENT_TYPES.WORLDPAY:
        case PAYMENT_TYPES.WORLDPAY_TRIPOS:
          paymentOptions = handleSwiperPayment(payment)
          break
        case PAYMENT_TYPES.LSPAY:
          paymentOptions = handleLSPay(payment)
          break
        // TODO: handle LSpay Ecom refund
        case PAYMENT_TYPES.LOYALTY:
          paymentOptions = handleLoyaltyPayment(payment)
          break
        // TODO: Handle gift card
        case PAYMENT_TYPES.STORE_CREDIT:
          paymentOptions = handleStoreCreditPayment(payment)
          break
        case PAYMENT_TYPES.CASH_CONCEALED:
        case PAYMENT_TYPES.CURRENCY:
        case PAYMENT_TYPES.CASH:
          paymentOptions = handleCashPayment(payment)
          break
        case PAYMENT_TYPES.BIGCOMMERCE_GIFT_CERT:
          paymentOptions = handleBigCommercePayment(payment)
          break
        default:
          paymentOptions = defaultPaymentHandler(payment)
          break
      }
      if (!paymentOptions) {
        return
      }
      setSelectedPaymentType({
        paymentOptions,
      })
    },
    [
      defaultPaymentHandler,
      handleBigCommercePayment,
      handleCashPayment,
      handleLSPay,
      handleLoyaltyPayment,
      handleStoreCreditPayment,
      handleSwiperPayment,
      handlePaymentTracking,
      setPaymentProcessing,
      hasPaymentAmountError,
      paymentProcessing,
    ]
  )

  return {
    selectedPaymentType,
    handlePayment,
    handleCompletePayment,
    clearSelectedPaymentType,
  }
}
